import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { SectionHeading, TextField, ToggleRow } from 'src/components/app-ui'
import usePathSettings, { usePersistSetting } from '../hooks/usePathSettings'
import useQueuedSave, { jsonEquals } from 'src/hooks/useQueuedSave'

const TRIGGERS = [
  {
    trigger: 0,
    title: 'Path started',
    description:
      'Fires when a learner begins their first course in this path.',
  },
  {
    trigger: 1,
    title: 'Path completed',
    description:
      'Fires when a learner completes all courses in this path.',
  },
]

const DEFAULT_WEBHOOK = {
  url: '',
  isActive: true,
  triggers: TRIGGERS.map(({ trigger }) => ({ trigger, isActive: false })),
}

/** `webhookSettings` is stored as JSON (string or object). */
const parseWebhook = raw => {
  if (!raw) return DEFAULT_WEBHOOK
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return {
      url: parsed.url || '',
      isActive: parsed.isActive ?? true,
      triggers: parsed.triggers || DEFAULT_WEBHOOK.triggers,
    }
  } catch (error) {
    return DEFAULT_WEBHOOK
  }
}

const useStyles = makeStyles({
  url: {
    maxWidth: 640,
  },
  toggles: {
    display: 'flex',
    flexDirection: 'column',
    gap: '22px',
    marginTop: 4,
  },
})

const WebhookSection = () => {
  const classes = useStyles()
  const { settings } = usePathSettings()
  // Instant-save: quick trigger toggles queue up instead of undoing each other.
  const [webhook, save] = useQueuedSave(
    parseWebhook(settings.webhookSettings),
    usePersistSetting('webhookSettings'),
    jsonEquals,
  )
  const [url, setUrl] = useState(webhook.url)

  useEffect(() => {
    setUrl(webhook.url)
  }, [webhook.url])

  const isTriggerOn = trigger =>
    Boolean(webhook.triggers.find(t => t.trigger === trigger)?.isActive)

  const toggleTrigger = trigger =>
    save({
      ...webhook,
      triggers: webhook.triggers.map(t =>
        t.trigger === trigger ? { ...t, isActive: !t.isActive } : t,
      ),
    })

  return (
    <>
      <SectionHeading
        title="Webhook"
        description="Configure webhook integrations for this learning path to notify external services when learners interact with your courses."
      />
      <div className={classes.url}>
        <TextField
          placeholder="Webhook URL"
          value={url}
          onChange={event => setUrl(event.target.value)}
          onBlur={() => url !== webhook.url && save({ ...webhook, url })}
        />
      </div>
      <div className={classes.toggles}>
        {TRIGGERS.map(({ trigger, title, description }) => (
          <ToggleRow
            key={trigger}
            title={title}
            description={description}
            checked={isTriggerOn(trigger)}
            onChange={() => toggleTrigger(trigger)}
          />
        ))}
      </div>
    </>
  )
}

export default WebhookSection
